import { Paper } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { format } from "date-fns";

import { humanReadableDateTimeFormat } from "@dzambalaorg/constants";
import { RichTextDisplay } from "@dzambalaorg/mui-rte";
import { Table } from "@dzambalaorg/ui-kit";
import { formatItem, formatPenalty } from "@framework/exchange";
import type { IStakingDeposit } from "@framework/types";

import { normalizeDuration } from "../../../../../../utils/time";

export interface IStakesDialogViewProps {
  viewItem: IStakingDeposit;
}

export const StakesDialogView = (props: IStakesDialogViewProps) => {
  const { viewItem } = props;

  const { stakingRule, depositAsset, startTimestamp, withdrawTimestamp } = viewItem;
  const { penalty } = stakingRule || { penalty: 0 };

  const { formatMessage } = useIntl();

  return (
    <Table.Container component={Paper}>
      <Table aria-label="table">
        <Table.Body>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.title" />
            </Table.Cell>
            <Table.Cell align="right">{stakingRule?.title}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.description" />
            </Table.Cell>
            <Table.Cell align="right">
              <RichTextDisplay data={stakingRule?.description} />
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.contract" />
            </Table.Cell>
            <Table.Cell align="right">{stakingRule?.contract!.title}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.deposit" />
            </Table.Cell>
            <Table.Cell align="right">{formatItem(depositAsset)}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.reward" />
            </Table.Cell>
            <Table.Cell align="right">{formatItem(stakingRule?.reward)}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.durationAmount" />
            </Table.Cell>
            <Table.Cell align="right">
              {stakingRule?.durationAmount && stakingRule?.durationUnit
                ? formatMessage(
                    { id: `enums.durationUnit.${stakingRule.durationUnit}` },
                    {
                      count: normalizeDuration({
                        durationAmount: stakingRule.durationAmount,
                        durationUnit: stakingRule.durationUnit,
                      }),
                    },
                  )
                : null}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.startTimestamp" />
            </Table.Cell>
            <Table.Cell align="right">
              {startTimestamp ? format(new Date(startTimestamp), humanReadableDateTimeFormat) : null}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.endTimestamp" />
            </Table.Cell>
            <Table.Cell align="right">
              {withdrawTimestamp ? format(new Date(withdrawTimestamp), humanReadableDateTimeFormat) : null}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.recurrent" />
            </Table.Cell>
            <Table.Cell align="right">{stakingRule?.recurrent ? "yes" : "no"}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell component="th" scope="row">
              <FormattedMessage id="form.labels.penalty" />
            </Table.Cell>
            <Table.Cell align="right">{formatPenalty(penalty)}%</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </Table.Container>
  );
};
